import { FunctionComponent, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { FeedPerson } from "@signalist/shared-types";

import { Spinner } from "@/components/Spinner/Spinner";

import { useGetSingleListPersonQuery } from "@/pages/Private/redux/list/list.api";

import { ReachOutAiPanel } from "@/pages/Private/pages/List/components/ReachOut/ReachOutAiPanel";

// Same footprint as the list reach-out menu's expanded GenerateBar view.
const PANEL_WIDTH = 640;
const MARGIN = 8;

interface SignalGeneratePopoverProps {
	/** The card's currently selected person — the generation target (D3). */
	person: FeedPerson;
	/** Trigger rect (viewport coords) the panel anchors to. */
	anchorRect: DOMRect;
	/** The signal line seeding GenerateBar's instruction input. */
	seedText: string;
	/** Maps the job back to the seeding signal row (D4). */
	seedSignalId: number;
	onClose: () => void;
}

/**
 * The per-signal "Let AI draft" panel (PREC-3635 P6, revised): the shared
 * {@link ReachOutAiPanel} (full GenerateBar) seeded by the signal line +
 * `seedSignalId`, anchored under the row CTA. Positioning and the backdrop
 * click-catcher mirror the list `ReachOutMenu` — GenerateBar's body-portaled
 * dropdowns sit above their own backdrop, so a global outside-click listener
 * would dismiss the panel mid-interaction.
 */
export const SignalGeneratePopover: FunctionComponent<SignalGeneratePopoverProps> = ({
	person,
	anchorRect,
	seedText,
	seedSignalId,
	onClose,
}) => {
	const { t } = useTranslation();
	const [panelEl, setPanelEl] = useState<HTMLDivElement | null>(null);
	const [position, setPosition] = useState<{ top: number; left: number }>({
		top: -9999,
		left: -9999,
	});

	const { data: listPerson } = useGetSingleListPersonQuery(person.listPersonId ?? 0, {
		skip: !person.listPersonId,
	});

	// Below the trigger, flipped above when it would clip the viewport bottom,
	// clamped horizontally (list ReachOutMenu positioning contract).
	useLayoutEffect(() => {
		if (!panelEl) {
			return;
		}

		const height = panelEl.offsetHeight;
		let top = anchorRect.bottom + MARGIN;

		if (top + height > window.innerHeight - MARGIN) {
			top = anchorRect.top - height - MARGIN;
		}

		top = Math.max(MARGIN, top);

		const left = Math.max(
			MARGIN,
			Math.min(anchorRect.left, window.innerWidth - PANEL_WIDTH - MARGIN)
		);

		setPosition({ top, left });
	}, [anchorRect, panelEl, listPerson]);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose();
			}
		};

		document.addEventListener("keydown", onKey);

		return () => document.removeEventListener("keydown", onKey);
	}, [onClose]);

	return createPortal(
		<>
			{/* Click-catcher below the panel — GenerateBar's own portaled dropdowns
			    (higher z, own backdrop) stay clickable without dismissing it. */}
			<div className="fixed inset-0 z-[1190]" onMouseDown={onClose} />
			<div
				ref={setPanelEl}
				className="fixed z-[1200] flex max-h-[calc(100vh-16px)] flex-col overflow-y-auto rounded-xl border border-border-light bg-white p-1.5 shadow-lg"
				data-testid="feed-signal-generate-popover"
				style={{ top: position.top, left: position.left, width: PANEL_WIDTH }}
				onClick={(event) => event.stopPropagation()}
			>
				<div className="px-2.5 pb-1.5 pt-2 text-[11px] font-semibold uppercase tracking-wide text-text-disabled">
					{t("feed.signalCta.draft")}
				</div>
				{listPerson ? (
					<ReachOutAiPanel
						seedSignalId={seedSignalId}
						target={{ kind: "lead", row: listPerson }}
						triggerDescription={seedText}
						onGenerated={onClose}
					/>
				) : (
					<div className="flex items-center justify-center py-6">
						<Spinner color="#4d66f5" />
					</div>
				)}
			</div>
		</>,
		document.body
	);
};
